/**
 * Model Package Export/Import admin page (issue #786, epic #777).
 *
 * Export: trigger a full-model export, poll DynamoDB-backed progress while
 * the long POST is in flight (mirrors `routes/admin/migration`'s polling
 * pattern), then download the resulting `.zip` via the signed URL.
 *
 * Import: upload a `.zip` package, run a zero-write dry-run preview (counts,
 * collisions, id remaps), require an explicit destructive-overwrite
 * confirmation, then execute with live progress + a result summary.
 *
 * Backend contract: `backend/src/routes/modelExport.ts` +
 * `backend/src/routes/modelPackage.ts` (issues #783/#784/#785/#787).
 */
import { createFileRoute } from '@tanstack/react-router';
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePermission } from '../../../hooks/usePermission';
import { selectToken } from '../../../store/features/auth/authSlice';
import { DataTable } from '../../../components/shared/DataTable';
import { DeleteConfirmationDialog } from '../../../components/shared/DeleteConfirmationDialog';
import { trackEvent } from '../../../lib/usageEventBatch';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import '@tylertech/forge/card';
import '@tylertech/forge/linear-progress';
import '@tylertech/forge/icon';
import '@tylertech/forge/checkbox';
import './index.css';

const REST_API_BASE = import.meta.env.VITE_REST_API_URL || 'http://localhost:3001/api/v1';
const MODEL_PACKAGE_BASE = `${REST_API_BASE}/admin/model-package`;

// Poll cadence — mirrors routes/admin/migration's tuning (see that module's
// doc comment for the full rationale: long-running steps, 429/5xx backoff).
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_INTERVAL_MS = 30_000;
const BACKOFF_MULTIPLIER = 2;
const MAX_CONSECUTIVE_POLL_404 = 5;

// Cap on remap rows rendered — the backend preview already caps at 200
// (`DRY_RUN_REMAP_PREVIEW_MAX_ENTRIES`); this is a defensive UI-side cap too.
const REMAP_TABLE_MAX_ROWS = 200;

export const Route = createFileRoute('/admin/model-package/')({
  component: ModelPackageAdminPage,
});

export { ModelPackageAdminPage };

// ── Types ────────────────────────────────────────────────────────────────

interface PackageManifest {
  schemaVersion: string;
  sourceEnv: string;
  exportedAt: string;
  entityCounts: Record<string, number>;
  edgeCount: number;
  checksum: string;
  checksumAlgorithm: string;
}

interface ExportResult {
  operationId: string;
  downloadUrl: string;
  manifest: PackageManifest;
  expiresInSeconds: number;
}

interface DryRunRemap {
  entityType: string;
  sourceId: string;
  targetId: string;
}

interface DryRunPreview {
  entityCounts: Record<string, number>;
  relationshipCount: number;
  remaps: DryRunRemap[];
  remappedCount: number;
  unresolvedRefs: number;
}

interface DryRunResult {
  manifest: PackageManifest;
  preview: DryRunPreview;
  confirmationToken: string;
  expiresAt: string;
}

interface PreImportBackupRef {
  operationId: string;
  key: string;
  downloadUrl?: string;
  entityCounts: Record<string, number>;
  edgeCount: number;
}

interface ImportResult {
  operationId: string;
  entities: { processed: number; remapped: number; failed: number };
  relationships: { processed: number; failed: number; unresolvedRefs: number };
  idMap: Record<string, string>;
  preImportBackup: PreImportBackupRef;
}

interface ProgressSnapshot {
  processedItems: number;
  totalItems: number;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
}

interface ApiErrorBody {
  error?: { code?: string; message?: string };
  errors?: Array<{ code?: string; message?: string }>;
}

type AsyncStatus = 'idle' | 'running' | 'done' | 'error';

// ── Progress polling (mirrors routes/admin/migration's pollOperationProgress) ─

/** Exported for unit testing; production callers always use `Math.random`. */
export function computeBackoffDelay(
  prevDelayMs: number,
  retryAfterHeader: string | null,
  randomFn: () => number = Math.random
): number {
  if (retryAfterHeader) {
    const seconds = Number(retryAfterHeader);
    if (Number.isFinite(seconds) && seconds > 0) {
      return Math.min(seconds * 1000, MAX_POLL_INTERVAL_MS);
    }
  }
  const grown = Math.min(prevDelayMs * BACKOFF_MULTIPLIER, MAX_POLL_INTERVAL_MS);
  const jitter = randomFn() * POLL_INTERVAL_MS;
  return Math.min(grown + jitter, MAX_POLL_INTERVAL_MS);
}

function interruptibleDelay(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });
}

/**
 * Poll `GET {operationsUrl}/{operationId}` until aborted or a terminal status
 * is reached. Exported for unit testing; `runExport`/`runImport` are the only
 * production callers.
 */
export async function pollOperationProgress(
  operationsUrl: string,
  operationId: string,
  headers: Record<string, string>,
  onProgress: (p: { done: number; total: number }) => void,
  signal: AbortSignal
): Promise<void> {
  const url = `${operationsUrl}/${operationId}`;
  let consecutive404 = 0;
  let delayMs = POLL_INTERVAL_MS;
  while (!signal.aborted) {
    await interruptibleDelay(delayMs, signal);
    if (signal.aborted) break;
    try {
      const pr = await fetch(url, { headers });
      if (!pr.ok) {
        if (pr.status === 404 && ++consecutive404 >= MAX_CONSECUTIVE_POLL_404) break;
        if (pr.status === 429 || pr.status >= 500) {
          delayMs = computeBackoffDelay(delayMs, pr.headers?.get('Retry-After') ?? null);
        }
        continue;
      }
      consecutive404 = 0;
      delayMs = POLL_INTERVAL_MS;
      const { data } = (await pr.json()) as { data?: ProgressSnapshot };
      if (data) {
        onProgress({ done: data.processedItems ?? 0, total: data.totalItems ?? 0 });
        if (data.status === 'COMPLETED' || data.status === 'FAILED') break;
      }
    } catch {
      // Transient poll failure — keep polling; the POST result is authoritative.
    }
  }
}

// ── File → base64 (mirrors routes/admin/migration's chunked encoder) ──────

function readFileAsBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const bytes = new Uint8Array(reader.result as ArrayBuffer);
      let binary = '';
      const chunk = 8192;
      for (let i = 0; i < bytes.length; i += chunk) {
        binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
      }
      resolve(btoa(binary));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

async function parseErrorBody(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    return (
      body.error?.message ?? body.errors?.[0]?.message ?? `Request failed (HTTP ${res.status})`
    );
  } catch {
    return `Request failed (HTTP ${res.status})`;
  }
}

function totalEntityCount(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

// ── Component ────────────────────────────────────────────────────────────

function ModelPackageAdminPage(): JSX.Element {
  const canExport = usePermission('model-package:export');
  const canImport = usePermission('model-package:import');
  const BannerElement = 'forge-banner' as React.ElementType;

  if (!canExport && !canImport) {
    return (
      <div className="model-package-page">
        <h1 className="forge-typography--headline5">Export / Import Model Package</h1>
        <BannerElement theme="error" dismissed={false} role="alert">
          <span>Access Restricted — Administrator role required.</span>
        </BannerElement>
      </div>
    );
  }

  return (
    <div className="model-package-page">
      <h1 className="forge-typography--headline5">Export / Import Model Package</h1>
      <p className="forge-typography--body1 model-package-subtitle">
        Export the full EA model to a downloadable <code>.zip</code>, or import a package into this
        environment. Import overwrites this environment&rsquo;s current content.
      </p>

      {canExport && <ExportCard />}
      {canImport && <ImportCard />}
    </div>
  );
}

// ── Export ───────────────────────────────────────────────────────────────

function ExportCard(): JSX.Element {
  const ForgeButton = 'forge-button' as React.ElementType;
  const ForgeCard = 'forge-card' as React.ElementType;
  const ForgeLinearProgress = 'forge-linear-progress' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const authToken = useSelector(selectToken);

  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ExportResult | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const authHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    return headers;
  }, [authToken]);

  const handleExport = useCallback(async (): Promise<void> => {
    setStatus('running');
    setError(null);
    setResult(null);
    setProgress({ done: 0, total: 0 });
    trackEvent('feature.model_package_export_started');

    const operationId = crypto.randomUUID();
    const headers = authHeaders();
    const pollController = new AbortController();
    const pollLoop = pollOperationProgress(
      `${MODEL_PACKAGE_BASE}/export/operations`,
      operationId,
      headers,
      setProgress,
      pollController.signal
    );

    try {
      const res = await fetch(`${MODEL_PACKAGE_BASE}/export`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ operationId }),
      });
      if (!res.ok) {
        setError(await parseErrorBody(res));
        setStatus('error');
        return;
      }
      const { data } = (await res.json()) as { data: ExportResult };
      setResult(data);
      setStatus('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export network error');
      setStatus('error');
    } finally {
      pollController.abort();
      await pollLoop;
      setProgress(null);
    }
  }, [authHeaders]);

  const pct =
    progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;

  return (
    <ForgeCard className="model-package-card">
      <h2 className="forge-typography--subtitle1">Export</h2>
      <p className="forge-typography--body2 model-package-card__description">
        Exports every entity and relationship in this environment&rsquo;s model to a{' '}
        <code>.zip</code> package.
      </p>

      <div className="model-package-actions">
        <ForgeButton
          type="button"
          variant="raised"
          disabled={status === 'running'}
          onClick={handleExport}
          aria-label="Export model package"
        >
          <forge-icon slot="start" name="export" aria-hidden="true"></forge-icon>
          {status === 'running' ? 'Exporting…' : 'Export Model'}
        </ForgeButton>

        {status === 'running' && progress && (
          <div className="model-package-progress">
            <ForgeLinearProgress
              determinate
              progress={progress.total > 0 ? progress.done / progress.total : 0}
              className="model-package-progress__bar"
            />
            <span className="model-package-progress__label">
              {progress.done} / {progress.total} {pct !== null ? `(${pct}%)` : ''}
            </span>
          </div>
        )}
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="model-package-error" role="alert">
          <span>{error}</span>
        </BannerElement>
      )}

      {status === 'done' && result && (
        <div className="detail-grid model-package-result">
          <div className="detail-row">
            <span className="detail-label">Entities</span>
            <span className="detail-value">{totalEntityCount(result.manifest.entityCounts)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Relationships</span>
            <span className="detail-value">{result.manifest.edgeCount}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Exported At</span>
            <span className="detail-value">{result.manifest.exportedAt}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Checksum</span>
            <span className="detail-value detail-value-code">{result.manifest.checksum}</span>
          </div>
          <div className="detail-row">
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => window.open(result.downloadUrl, '_blank', 'noopener,noreferrer')}
              aria-label="Download model package"
            >
              <forge-icon slot="start" name="download" aria-hidden="true"></forge-icon>
              Download Package
            </ForgeButton>
          </div>
        </div>
      )}
    </ForgeCard>
  );
}

// ── Import ───────────────────────────────────────────────────────────────

function ImportCard(): JSX.Element {
  const ForgeButton = 'forge-button' as React.ElementType;
  const ForgeCard = 'forge-card' as React.ElementType;
  const ForgeLinearProgress = 'forge-linear-progress' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;
  const authToken = useSelector(selectToken);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [dryRunStatus, setDryRunStatus] = useState<AsyncStatus>('idle');
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importStatus, setImportStatus] = useState<AsyncStatus>('idle');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const authHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    return headers;
  }, [authToken]);

  const resetForNewFile = useCallback((): void => {
    setDryRun(null);
    setDryRunStatus('idle');
    setImportResult(null);
    setImportStatus('idle');
    setError(null);
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setFile(e.target.files?.[0] ?? null);
      resetForNewFile();
    },
    [resetForNewFile]
  );

  const handleDryRun = useCallback(async (): Promise<void> => {
    if (!file) return;
    setDryRunStatus('running');
    setError(null);
    setImportResult(null);
    setImportStatus('idle');

    try {
      const packageBase64 = await readFileAsBase64(file);
      const res = await fetch(`${MODEL_PACKAGE_BASE}/import/dry-run`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ packageBase64 }),
      });
      if (!res.ok) {
        setError(await parseErrorBody(res));
        setDryRunStatus('error');
        return;
      }
      const { data } = (await res.json()) as { data: DryRunResult };
      setDryRun(data);
      setDryRunStatus('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Dry-run network error');
      setDryRunStatus('error');
    }
  }, [file, authHeaders]);

  const executeImport = useCallback(async (): Promise<void> => {
    if (!file || !dryRun) return;
    setConfirmOpen(false);
    setImportStatus('running');
    setError(null);
    setProgress({ done: 0, total: 0 });
    trackEvent('feature.model_package_import_started');

    const operationId = crypto.randomUUID();
    const headers = authHeaders();
    const pollController = new AbortController();
    const pollLoop = pollOperationProgress(
      `${MODEL_PACKAGE_BASE}/operations`,
      operationId,
      headers,
      setProgress,
      pollController.signal
    );

    try {
      const packageBase64 = await readFileAsBase64(file);
      const res = await fetch(`${MODEL_PACKAGE_BASE}/import`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          packageBase64,
          operationId,
          confirmationToken: dryRun.confirmationToken,
        }),
      });
      if (!res.ok) {
        setError(await parseErrorBody(res));
        setImportStatus('error');
        return;
      }
      const { data } = (await res.json()) as { data: ImportResult };
      setImportResult(data);
      setImportStatus('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import network error');
      setImportStatus('error');
    } finally {
      pollController.abort();
      await pollLoop;
      setProgress(null);
    }
  }, [file, dryRun, authHeaders]);

  const pct =
    progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : null;

  return (
    <ForgeCard className="model-package-card">
      <h2 className="forge-typography--subtitle1">Import</h2>
      <p className="forge-typography--body2 model-package-card__description">
        Uploads a <code>.zip</code> model package, previews what would change, and — once confirmed
        — overwrites this environment&rsquo;s content.
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip"
        aria-label="Select model package file"
        className="model-package-file-input--hidden"
        onChange={handleFileChange}
      />

      <div className="model-package-actions">
        <ForgeButton type="button" variant="outlined" onClick={() => fileInputRef.current?.click()}>
          <forge-icon slot="start" name="upload_file" aria-hidden="true"></forge-icon>
          {file ? file.name : 'Choose Package…'}
        </ForgeButton>
        <ForgeButton
          type="button"
          variant="raised"
          disabled={!file || dryRunStatus === 'running' || importStatus === 'running'}
          onClick={handleDryRun}
          aria-label="Preview import (dry run)"
        >
          <forge-icon slot="start" name="preview" aria-hidden="true"></forge-icon>
          {dryRunStatus === 'running' ? 'Previewing…' : 'Preview Import'}
        </ForgeButton>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="model-package-error" role="alert">
          <span>{error}</span>
        </BannerElement>
      )}

      {dryRunStatus === 'done' && dryRun && importStatus !== 'done' && (
        <div className="model-package-preview">
          <h3 className="forge-typography--subtitle2">Preview</h3>
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Entities in package</span>
              <span className="detail-value">{totalEntityCount(dryRun.preview.entityCounts)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Relationships in package</span>
              <span className="detail-value">{dryRun.preview.relationshipCount}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">ID collisions (remaps)</span>
              <span className="detail-value">{dryRun.preview.remappedCount}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Unresolved references</span>
              <span
                className={
                  dryRun.preview.unresolvedRefs > 0
                    ? 'detail-value detail-value-error'
                    : 'detail-value'
                }
              >
                {dryRun.preview.unresolvedRefs}
              </span>
            </div>
          </div>

          {dryRun.preview.remaps.length > 0 && (
            <DataTable<DryRunRemap>
              columns={[
                { key: 'entityType', label: 'Type', accessor: (row) => row.entityType },
                { key: 'sourceId', label: 'Source ID', accessor: (row) => row.sourceId },
                { key: 'targetId', label: 'Target ID', accessor: (row) => row.targetId },
              ]}
              data={dryRun.preview.remaps.slice(0, REMAP_TABLE_MAX_ROWS)}
              getRowKey={(row) => `${row.entityType}:${row.sourceId}`}
            />
          )}

          <ForgeButton
            type="button"
            variant="raised"
            className="model-package-import-btn--danger"
            onClick={() => setConfirmOpen(true)}
            aria-label="Confirm and execute import"
          >
            <forge-icon slot="start" name="warning" aria-hidden="true"></forge-icon>
            Confirm &amp; Import
          </ForgeButton>
        </div>
      )}

      {importStatus === 'running' && progress && (
        <div className="model-package-progress">
          <ForgeLinearProgress
            determinate
            progress={progress.total > 0 ? progress.done / progress.total : 0}
            className="model-package-progress__bar"
          />
          <span className="model-package-progress__label">
            {progress.done} / {progress.total} {pct !== null ? `(${pct}%)` : ''}
          </span>
        </div>
      )}

      {importStatus === 'done' && importResult && (
        <div className="detail-grid model-package-result">
          <div className="detail-row">
            <span className="detail-label">Entities processed</span>
            <span className="detail-value">
              {importResult.entities.processed} ({importResult.entities.remapped} remapped,{' '}
              {importResult.entities.failed} failed)
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Relationships processed</span>
            <span className="detail-value">
              {importResult.relationships.processed} ({importResult.relationships.failed} failed,{' '}
              {importResult.relationships.unresolvedRefs} unresolved)
            </span>
          </div>
          {importResult.preImportBackup.downloadUrl && (
            <div className="detail-row">
              <span className="detail-label">Pre-import backup</span>
              <span className="detail-value">
                <a
                  href={importResult.preImportBackup.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download backup package
                </a>
              </span>
            </div>
          )}
        </div>
      )}

      <DeleteConfirmationDialog
        open={confirmOpen}
        title="Confirm Model Import"
        message="This will overwrite all entities and relationships currently in this environment with the contents of the uploaded package. Colliding IDs will be remapped. A pre-import backup will be captured automatically, but this action cannot be undone from this screen."
        warning="This is a destructive, environment-wide overwrite."
        itemName={file?.name ?? 'model package'}
        onConfirm={executeImport}
        onCancel={() => setConfirmOpen(false)}
        isDeleting={importStatus === 'running'}
        confirmLabel="Overwrite & Import"
        confirmingLabel="Importing..."
      />
    </ForgeCard>
  );
}
